// src/pages/AdminsLogin.js
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import ErrorBox from "../../components/ErrorBox";
import InputLabel from "../../components/InputLabel";

import LanguageSwitcher from "../../components/LanguageSwitcher";
import logo from "../../assets/logo.png";
import Modal from "../../components/Modal";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import PasswordInput from "../../components/PasswordInput";

const AdminsLogin = ({ isAuthenticatedAdmin, setIsAuthenticatedAdmin }) => {
  const { t } = useTranslation("general");
  const [loading, setLoading] = useState(false);
  const [superAdminLoading, setSuperAdminLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuperAdminLoginModal, setShowSuperAdminLoginModal] =
    useState(false); // New state for modal visibility
  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      setError(true);
      setErrorMessage(t("emptyInputFields"));
      return;
    }

    // Validate email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError(true);
      setErrorMessage(t("invalidEmail"));
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/adminlogin`,
        { email, password }
      );

      const { token, user } = response.data;
      localStorage.setItem("userType", "admin");
      localStorage.setItem("token", token);
      localStorage.setItem("adminToken", token); // Also store as adminToken
      localStorage.setItem("userId", user.id);
      localStorage.setItem("email", user.email);
      localStorage.setItem("username", user.username);
      setIsAuthenticatedAdmin(token);
      navigate("/controlPanel");
    } catch (error) {
      console.error("Login error:", error);
      setError(true);
      setErrorMessage(t(error.response?.data?.error) || t("loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSuperAdminLogin = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      setError(true);
      setErrorMessage(t("emptyInputFields"));
      return;
    }

    setSuperAdminLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/superAdminlogin`,
        { email, password }
      );

      const { token, user } = response.data;
      // Store all necessary data
      localStorage.setItem("userType", "superadmin");
      localStorage.setItem("SuperAdminToken", token);
      localStorage.setItem("token", token);
      localStorage.setItem("userId", user?.id || "super-admin-id");
      localStorage.setItem("email", user?.email || email);
      localStorage.setItem("username", user?.username || "Super-Admin");

      setIsAuthenticatedAdmin(token);
      navigate("/iraqi-banks-page");
    } catch (error) {
      console.error("Super Admin login error:", error);
      setError(true);
      setErrorMessage(error.response?.data?.error || t("loginFailed"));
    } finally {
      setSuperAdminLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuperAdminLoginModal(false);
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding">
        <div className="main-wrapper">
          <div className="language-switcher-container">
            <div className="language-switcher">
              <LanguageSwitcher />
            </div>
          </div>
          <div>
            <img src={logo} alt="Ehsibly" className="website-logo" />
            <p className="intro-text">
              <span className="bold-text">{t("websiteTitle")} </span>
              {t("websiteDescription")}
            </p>
          </div>

          <form className="rigister-login-form" id="login-form">
            <h2>{t("login")}</h2>
            {error && <ErrorBox>{errorMessage}</ErrorBox>}

            <div className="inputContainer">
              <InputLabel title={t("email")} className="label" />
              <CustomInput
                placeholder={t("email")}
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                errorText={error && !email ? t("inputFieldCannotBeEmpty") : ""}
              />
            </div>

            <div className="inputContainer">
              <PasswordInput
                title={t("password")}
                placeholder={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                errorText={error && !password ? "inputFieldCannotBeEmpty" : ""}
              />
            </div>

            <div className="o-row o-row--horizontal-align_center u-width_full-width">
              {loading ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
            </div>

            <CustomButton
              title={loading ? t("loading") : t("login")}
              onClick={(e) => handleLogin(e)}
              className="onboarding-button"
              disabled={loading}
            />

            <button
              id="forgot-password-button"
              className={"forgetPasswordTextButton"}
              type="button"
              onClick={() => setShowSuperAdminLoginModal(true)} // Open modal
            >
              {t("loginAsSuperAdmin")}
            </button>

            {showSuperAdminLoginModal && (
              <Modal onClose={handleCloseModal}>
                <>
                  <h2>{t("superAdminLogin")}</h2>
                  {error && <ErrorBox>{errorMessage}</ErrorBox>}

                  <div className="inputContainer">
                    <InputLabel title={t("email")} className="label" />
                    <CustomInput
                      placeholder={t("email")}
                      value={email}
                      type="email"
                      onChange={(e) => setEmail(e.target.value)}
                      errorText={
                        error && !email ? t("inputFieldCannotBeEmpty") : ""
                      }
                    />
                  </div>

                  <div className="inputContainer">
                    <PasswordInput
                      title={t("password")}
                      placeholder={t("password")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      errorText={
                        error && !password ? "inputFieldCannotBeEmpty" : ""
                      }
                    />
                  </div>
                  {superAdminLoading ? <Loader /> : null}
                  <CustomButton
                    title={superAdminLoading ? t("loading") : t("login")}
                    onClick={(e) => handleSuperAdminLogin(e)}
                    className="onboarding-button"
                    disabled={superAdminLoading}
                  />
                </>
              </Modal>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminsLogin;
